const GTM_SCRIPT_ID = "gtm-script";

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

const ensureDataLayer = () => {
  if (typeof window === "undefined") {
    return;
  }
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
};

const injectGtmScript = (language: string) => {
  if (typeof document === "undefined") {
    return;
  }

  const hasDebugGtm = window.location.search.includes("debugGtm=true");

  // Solo en producción (no en localhost), a menos que queramos depurar con la carga real de Google
  if ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && !hasDebugGtm) {
    return;
  }
  if (document.getElementById(GTM_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement("script");
  script.async = true;

  if (hasDebugGtm) {
    // Carga REAL directa de Google GTM en localhost para auditar contenedores
    const gtmId = language === "en" ? "GTM-WNJZTLL8" : "GTM-NMXMBT3";
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    console.log(
      `%c[GTM Debug] 🌐 Cargando contenedor REAL de Google (${language.toUpperCase()}): ${gtmId}`,
      "color: #8B5CF6; font-weight: bold; background: #F3E8FF; padding: 2px 6px; border-radius: 4px;"
    );
  } else {
    // Carga GTM directamente de Google
    const gtmId = language === "en"
      ? (process.env.REACT_APP_GTM_ID_EN || "GTM-WNJZTLL8")
      : (process.env.REACT_APP_GTM_ID_ES || "GTM-NMXMBT3");
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  }

  script.id = GTM_SCRIPT_ID;
  document.head.appendChild(script);
};

export const initGtm = (language?: "en" | "es") => {
  if (typeof window === "undefined") {
    return;
  }

  const currentLanguage = language || "es";

  ensureDataLayer();
  // Si el usuario ya aceptó en esta sesión (recarga), restaura el consentimiento antes de cargar GTM.
  if (hasTrackingConsent()) {
    sendConsentUpdate(true);
  }
  window.dataLayer!.push({
    "gtm.start": Date.now(),
    event: "gtm.js",
  });

  injectGtmScript(currentLanguage);
};

export const isDebugEnabled = (): boolean => {
  if (typeof window === "undefined") return false;
  return (
    process.env.NODE_ENV === "development" ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.search.includes("debug=true") ||
    (typeof localStorage !== "undefined" && localStorage.getItem("debug") === "true")
  );
};

export const getGoogleAdsPhoneNumber = (language: string): string | null => {
  if (typeof document === "undefined") return null;

  // Intenta obtener el número reemplazado por Google Ads
  const phoneLink = document.querySelector(
    `a[data-google-ads-phone="${language}"]`
  ) as HTMLAnchorElement | null;

  if (phoneLink && phoneLink.href) {
    const match = phoneLink.href.match(/tel:(.+)/);
    if (match) {
      return match[1];
    }
  }

  return null;
};

// Consentimiento del usuario para compartir sus datos de contacto con las
// herramientas de medición/publicidad (GTM). Se otorga al aceptar términos y
// política de privacidad en el formulario; sin él, `user_data` nunca llega al dataLayer.
const CONSENT_STORAGE_KEY = "tracking_consent";

const sendConsentUpdate = (granted: boolean) => {
  const state = granted ? "granted" : "denied";
  ensureDataLayer();
  // Consent Mode exige el objeto `arguments` (formato gtag), no un objeto literal.
  const gtagFn =
    (window as any).gtag ||
    function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments as any);
    };
  gtagFn("consent", "update", {
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
    analytics_storage: state,
  });
};

export const setTrackingConsent = (granted: boolean) => {
  if (typeof window === "undefined") return;
  sendConsentUpdate(granted);
  try {
    if (granted) {
      window.sessionStorage.setItem(CONSENT_STORAGE_KEY, "granted");
    } else {
      window.sessionStorage.removeItem(CONSENT_STORAGE_KEY);
    }
  } catch {
    // sessionStorage no disponible: queda sin consentimiento
  }
};

export const hasTrackingConsent = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(CONSENT_STORAGE_KEY) === "granted";
  } catch {
    return false;
  }
};

export const pushToDataLayer = (rawPayload: Record<string, unknown>) => {
  if (typeof window === "undefined") {
    return;
  }
  ensureDataLayer();

  // Sin consentimiento no se envían datos personales (email, teléfono, nombre, etc.).
  let payload = rawPayload;
  if ("user_data" in rawPayload && !hasTrackingConsent()) {
    const { user_data: _omitted, ...rest } = rawPayload;
    payload = rest;
  }
  window.dataLayer!.push(payload);

  // Mostrar notificaciones en la consola en modo de desarrollo o si el modo debug está activo
  if (isDebugEnabled()) {
    if (payload.event === "lead_started") {
      console.log(
        `%c[GTM Event] 📝 Lead Creado Exitosamente: ${payload.leadId}`,
        "color: #10B981; font-weight: bold; font-size: 13px; background: #ECFDF5; padding: 4px 8px; border-radius: 4px; border: 1px solid #10B981;",
        payload
      );
    } else {
      console.log(
        `%c[GTM dataLayer.push] 🚀 Evento: ${payload.event}`,
        "color: #6366F1; font-weight: bold; background: #EEF2FF; padding: 2px 6px; border-radius: 4px;",
        payload
      );
    }
  }
};

export const normalizeEmail = (email?: string): string | undefined => {
  if (!email) return undefined;
  return email.trim().toLowerCase();
};

export const normalizePhone = (phone?: string): string | undefined => {
  if (!phone) return undefined;
  const digits = phone.replace(/\D/g, "");
  if (!digits) return undefined;
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  return `+${digits}`;
};

export const trackFormSubmit = (
  eventName: string,
  email?: string,
  phoneNumber?: string
) => {
  const payload: Record<string, unknown> = {
    event: eventName,
  };

  const normEmail = normalizeEmail(email);
  const normPhone = normalizePhone(phoneNumber);

  if (normEmail || normPhone) {
    payload.user_data = {
      ...(normEmail && { email: normEmail }),
      ...(normPhone && { phone_number: normPhone }),
    };
  }

  pushToDataLayer(payload);
};

// Dispara una conversión de Google Ads vía gtag. `conversionId` debe venir en
// formato "AW-XXXXXXX/LABEL" (send_to); es el mismo formato que gtag.js espera.
export const trackAdsConversion = (
  conversionId?: string,
  extra?: Record<string, unknown>
) => {
  if (typeof (window as any).gtag !== 'function' || !conversionId) return;

  const payload = {
    send_to: conversionId,
    value: 1.0,
    currency: 'USD',
    ...extra,
  };

  (window as any).gtag('event', 'conversion', payload);

  if (isDebugEnabled()) {
    console.log(
      `%c[Google Ads Conversion] ✅ Tracked`,
      "color: #059669; font-weight: bold; background: #D1FAE5; padding: 2px 6px;",
      payload
    );
  }
};

export const trackPhoneCall = (phoneNumber: string, language?: string, conversionId?: string) => {
  const eventName = language === "es" ? "phone_call_es" : "phone_call_en";
  pushToDataLayer({
    event: eventName,
    phoneNumber,
    language,
    timestamp: new Date().toISOString(),
  });

  trackAdsConversion(conversionId, { value: 30, phone_number: phoneNumber });
};
